import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { InlineInheritIconButton } from '@components/core-ui/form-elements/buttons/InlineInheritIconButton/';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import DiagonalCross from '@components/icons/DiagonalCross.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { downloadCSV } from '@helpers/downloadCSV';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { Filter } from '@pages/User/MemberReportsPage/components/Filter';
import { InlineJobsTable } from '@pages/User/MemberReportsPage/components/InlineJobsTable';
import ArrowIcon from '@components/icons/MemberArrow.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { hashKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationsService, PrismaOrganizationRoleDto, TeamsService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { TJobsFilterUpdate, useJobsFilterQueryParams } from 'app/hooks/useJobsFilterQueryParams';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { organizationsKeys, TMemberReportsListParams } from 'app/queries/organizations/organizationsKeys';
import { Fragment, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { EntityIcon } from '@components/core-ui/EntityIcon/';
import { buildOrgMemberReportsRequest } from './api/buildOrgMemberReportsRequest';
import { createFetchOrgMemberReportsQueryFn } from './api/fetchOrgMemberReports';
import { buildMemberReportsCsvRows, MEMBER_REPORTS_CSV_HEADERS } from './helpers/buildMemberReportsCsvRows';
import { formatEditsPerPage } from './helpers/formatEditsPerPage';
import { CellContent } from './styles/CellContent';
import { CellNoEllipsisContent } from './styles/CellNoEllipsisContent';
import { FlexCellContent } from './styles/FlexCellContent';
import { MainContent } from './styles/MainContent';
import { StyledLink } from './styles/StyledLink';
import { TeamsCell } from './styles/TeamsCell';

const SEARCH_DEBOUNCE_MS = 300;

type TExpandedRow = {
  memberId: string;
  listKey: string;
};

export const MemberReportsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { me, organization } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const navigate = useNavigate();
  const {
    searchParams,
    page,
    pageSize,
    search,
    orderBy,
    orderType,
    setSearch,
    setPage,
    setPageSize,
    toggleSort,
    updateParams,
  } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const { jobsFilter: filter, applyJobsFilter } = useJobsFilterQueryParams({ searchParams, updateParams });
  const [expandedRow, setExpandedRow] = useState<TExpandedRow | null>(null);
  const sortingField = orderBy;
  const sortingReverse = orderType === 'desc';

  const shouldShowReports = !!organization?.permissions?.viewReports;

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const jobsFrom = filter.from ? String(filter.from.getTime()) : '';
  const jobsTo = filter.to ? String(filter.to.getTime()) : '';
  const listParams = useMemo<TMemberReportsListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch, jobsFrom, jobsTo }),
    [debouncedSearch, jobsFrom, jobsTo, orderBy, orderType, page, pageSize],
  );
  const reportsQueryKey = organizationsKeys.memberReportsList(organizationId, listParams);
  const listKey = hashKey(reportsQueryKey);
  const openedTableId = expandedRow?.listKey === listKey ? expandedRow.memberId : null;

  const reportsQuery = useQuery({
    queryKey: reportsQueryKey,
    queryFn: createFetchOrgMemberReportsQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].organizationId === organizationId ? previousData : undefined,
    enabled: organizationId !== '' && !isSearchSettling,
  });
  const members = reportsQuery.data?.data ?? [];
  const membersCount = reportsQuery.data?.count ?? 0;

  const setFilter = (update: TJobsFilterUpdate) =>
    applyJobsFilter(update, { onUnchanged: () => void reportsQuery.refetch() });

  const removeTeamMutation = useMutation({
    mutationFn: ({ team, userId }: { team: PrismaOrganizationRoleDto; userId: string }) =>
      call(() => TeamsService.removeTeamFromUser({ oid: organizationId, userId, organizationRoleId: team.id })),
    onError: () => toast.error('Failed to remove the member from the team'),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationsKeys.memberReportsByOrg(organizationId) }),
        queryClient.invalidateQueries({ queryKey: organizationsKeys.membersByOrg(organizationId) }),
      ]),
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        OrganizationsService.getOrgMemberReports(
          buildOrgMemberReportsRequest(organizationId, { ...listParams, page: 1, pageSize: Math.max(membersCount, 1) }),
        ),
      ),
    onSuccess: (response) => {
      downloadCSV(buildMemberReportsCsvRows(response.data, organizationId), MEMBER_REPORTS_CSV_HEADERS);
    },
    onError: () => toast.error('Failed to export member reports'),
  });

  const isLoading =
    reportsQuery.isFetching || isSearchSettling || removeTeamMutation.isPending || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || reportsQuery.isPlaceholderData || !reportsQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Member Reports - AutoScript</title>
      </Helmet>
      <Header
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              onClick={() => {
                navigate(routes.organizationMembers.make(organization?.slug ?? ''));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              onClick={() => {
                navigate(routes.invitations.make(organization?.slug ?? ''));
              }}
            >
              Invitations
            </SwitchButton>
            {shouldShowReports ? (
              <SwitchButton
                active
                onClick={() => {
                  navigate(routes.organizationMemberReports.make(organization?.slug ?? ''));
                }}
              >
                Reports
              </SwitchButton>
            ) : null}
            {shouldShowReports && organization?.featureFlags.ATTORNEY_VIEW_AVAILABLE ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationAttorneysReport.make(organization?.slug ?? ''));
                }}
              >
                Attorneys Report
              </SwitchButton>
            ) : null}
          </HeaderSwitchWrapper>,
        ]}
        title="Members"
        buttonLabel={members.length ? 'Export CSV' : undefined}
        buttonOnClick={members.length ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={exportMutation.isPending}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        <Filter setFilter={setFilter} filter={filter} />
        {reportsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load member reports.</span>
            <TextButton type="button" onClick={() => void reportsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th style={{ width: '1%' }}></Th>
                <SortableTh style={{ width: '20%' }} onClick={() => toggleSort('name')}>
                  Name
                  {sortingField === 'name' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <Th style={{ width: '50%' }}>Contacts</Th>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
                  Created
                  {sortingField === 'createdAt' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <Th style={{ width: '50%' }}>Teams</Th>
                <Th style={{ width: '1%' }}>Active Jobs</Th>
                <Th style={{ width: '1%' }}>Jobs Completed</Th>
                <Th style={{ width: '1%' }}>Edits Per Page</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {members.map((data) => {
                const teams = (data.teams ?? []).filter((team) => team.organizationId === organization?.id);

                return (
                  <Fragment key={data.id}>
                    <Tr>
                      <Td>
                        <CellContent>
                          <EntityIcon name={`${data.name} ${data.lastname}`} src={data.avatar ?? undefined} />
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          {data.id === me.id ? (
                            <StyledLink to={routes.accountPage.make()}>
                              {data.name} {data.lastname}
                            </StyledLink>
                          ) : (
                            <StyledLink to={routes.userDetails.make(organization?.slug ?? '', data.id)}>
                              {data.name} {data.lastname}
                            </StyledLink>
                          )}
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <span style={{ display: 'block' }}>{data.email}</span>
                          <span style={{ display: 'block' }}>{data.phone}</span>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <CellNoEllipsisContent>
                            {(teams || []).map((item) => (
                              <TeamsCell key={item.name} color={item.colour}>
                                {item.name}
                                {teams.length > 1 ? (
                                  <InlineInheritIconButton
                                    title="Remove from team"
                                    aria-label="Remove from team"
                                    disabled={removeTeamMutation.isPending}
                                    onClick={() => removeTeamMutation.mutate({ team: item, userId: data.id })}
                                  >
                                    <DiagonalCross />
                                  </InlineInheritIconButton>
                                ) : null}
                              </TeamsCell>
                            ))}
                          </CellNoEllipsisContent>
                        </CellContent>
                      </Td>
                      <Td>
                        <FlexCellContent>{data.activeJobs}</FlexCellContent>
                      </Td>
                      <Td>
                        <FlexCellContent>{data.completedJobs}</FlexCellContent>
                      </Td>
                      <Td>
                        <FlexCellContent>{formatEditsPerPage(data.totalEdits, data.totalPages)}</FlexCellContent>
                      </Td>
                      <Td>
                        <FlexCellContent>
                          {data.checkoutEvents?.length ? (
                            <ArrowIcon
                              onClick={() =>
                                setExpandedRow(openedTableId === data.id ? null : { memberId: data.id, listKey })
                              }
                              className={openedTableId === data.id ? 'arrow-icon active' : 'arrow-icon'}
                            />
                          ) : null}
                        </FlexCellContent>
                      </Td>
                    </Tr>
                    {openedTableId === data.id ? <InlineJobsTable data={data} /> : null}
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={membersCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </MainContent>
    </>
  );
};
